import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSession } from '@clerk/react';

import { LoginSessionTask } from '@/components/auth/LoginSessionTask';
import { AuthSignInCard } from '@/components/auth/AuthSignInCard';
import { resolveLoginSessionState } from '@/lib/auth/login-session';

type LoginSessionGateProps = {
  signInRedirectUrl: string;
  signUpRedirectUrl: string;
  authNextQuery: string;
  lockedInviteEmail?: string | null;
  inviteTripTitle?: string | null;
  autoSendInviteCode?: boolean;
};

/**
 * Decide what `/login` shows for the session this browser already holds.
 *
 * - A session that still owes a task (e.g. a forced password reset) gets
 *   Clerk's task screen. Showing the sign-in form instead was a dead end: every
 *   attempt failed with "You're already signed in."
 * - A session that was already active when the page opened goes straight to
 *   the destination. A sign-in completed on this page navigates itself through
 *   Clerk's decorated URL, so this deliberately does not react to that.
 * - Otherwise, the sign-in form.
 */
export function LoginSessionGate({
  signInRedirectUrl,
  signUpRedirectUrl,
  authNextQuery,
  lockedInviteEmail = null,
  inviteTripTitle = null,
  autoSendInviteCode = false,
}: LoginSessionGateProps) {
  const { session } = useSession();
  const state = resolveLoginSessionState(session);
  const [activeOnArrival] = useState(() => state.kind === 'active');

  if (state.kind === 'task') {
    return <LoginSessionTask taskKey={state.taskKey} redirectUrl={signInRedirectUrl} />;
  }
  if (state.kind === 'active' && activeOnArrival) {
    return <Navigate to={signInRedirectUrl} replace />;
  }
  return (
    <AuthSignInCard
      signInRedirectUrl={signInRedirectUrl}
      signUpRedirectUrl={signUpRedirectUrl}
      authNextQuery={authNextQuery}
      lockedInviteEmail={lockedInviteEmail}
      inviteTripTitle={inviteTripTitle}
      autoSendInviteCode={autoSendInviteCode}
    />
  );
}
